import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';
import Alert from '../../Alert';
import ReviewsTable from './ReviewsTable';
import ReviewDetailModal from './ReviewDetailModal';
import ReviewEligibleOrdersTable from './ReviewEligibleOrdersTable';
import './SellerReviews.css';

const REQUEST_REFUSAL_MESSAGES = {
  'not-eligible': 'This order is no longer eligible for a review request.',
  'no-recipient': 'No email address is available for this customer.',
};

const TABS = [
  {key: 'reviews', label: 'Customer Reviews'},
  {key: 'eligible', label: 'Review Requests'},
];

function responseError(body, fallback) {
  return body?.message || body?.error || fallback;
}

// The server builds this from the channel's storefront URL; only http(s) is ever linked.
function safePublicReviewsUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value) ? value : null;
}

function LoadingSkeleton() {
  return (
    <div className="seller-reviews__loading" role="status">
      <span className="sr-only">Loading reviews…</span>
      <div className="seller-reviews__skeleton" />
      <div className="seller-reviews__skeleton" />
      <div className="seller-reviews__skeleton" />
    </div>
  );
}

/**
 * Top-level React workspace for the seller reviews surface. Owns data fetching for
 * both the inbound-reviews list and the review-eligible-orders list, the summary
 * metrics header, tab switching, and the detail modal.
 */
export default function SellerReviewsManager() {
  const {fetch: authFetch, API_URL} = useAuth();
  const [tab, setTab] = useState('reviews');
  const tabRefs = useRef([]);

  // Arrow / Home / End key navigation across the tablist, per the ARIA
  // authoring-practices tabs pattern.
  const handleTabKeyDown = (event, index) => {
    const keys = {
      ArrowRight: (index + 1) % TABS.length,
      ArrowLeft: (index - 1 + TABS.length) % TABS.length,
      Home: 0,
      End: TABS.length - 1,
    };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const nextIndex = keys[event.key];
    setTab(TABS[nextIndex].key);
    tabRefs.current[nextIndex]?.focus();
  };

  const [reviews, setReviews] = useState([]);
  const [reviewsMeta, setReviewsMeta] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewsError, setReviewsError] = useState('');

  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [eligibleLoading, setEligibleLoading] = useState(true);
  const [eligibleError, setEligibleError] = useState('');

  const [selectedReview, setSelectedReview] = useState(null);
  const [actionMessage, setActionMessage] = useState('');

  const loadReviews = useCallback(async () => {
    setReviewsLoading(true);
    setReviewsError('');
    try {
      const res = await authFetch(`${API_URL}/v1/seller/reviews`);
      const body = await res.json();
      if (!res.ok) throw new Error(responseError(body, 'Failed to load reviews'));
      setReviews(body?.data || []);
      setReviewsMeta(body?.meta || null);
    } catch (error) {
      setReviewsError(error.message);
    } finally {
      setReviewsLoading(false);
    }
  }, [authFetch, API_URL]);

  const loadEligibleOrders = useCallback(async () => {
    setEligibleLoading(true);
    setEligibleError('');
    try {
      const res = await authFetch(`${API_URL}/v1/seller/reviews/eligible-orders`);
      const body = await res.json();
      if (!res.ok) throw new Error(responseError(body, 'Failed to load eligible orders'));
      setEligibleOrders(body?.data || []);
    } catch (error) {
      setEligibleError(error.message);
    } finally {
      setEligibleLoading(false);
    }
  }, [authFetch, API_URL]);

  useEffect(() => {
    loadReviews();
    loadEligibleOrders();
  }, [loadReviews, loadEligibleOrders]);

  const submitResponse = async (review, response) => {
    const isEdit = Boolean(review.response);
    const res = await authFetch(
      `${API_URL}/v1/seller/reviews/${review.uuid}/${isEdit ? 'response' : 'respond'}`,
      {method: isEdit ? 'PATCH' : 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({response})},
    );
    const body = await res.json();
    if (!res.ok) throw new Error(responseError(body, 'Failed to submit response'));

    // Optimistic local update, then close the modal.
    setReviews(previous => previous.map(item => (
      item.uuid === review.uuid ? {...item, response, ...body.data} : item
    )));
    if (isEdit) {
      setSelectedReview(previous => ({...previous, response, ...body.data}));
      setActionMessage('Your response has been updated.');
    } else {
      setSelectedReview(null);
      setActionMessage('Your response has been sent to the customer.');
    }
  };

  const requestReview = async order => {
    const res = await authFetch(
      `${API_URL}/v1/seller/reviews/request/${order.uuid}`,
      {method: 'POST'},
    );
    const body = await res.json();
    if (!res.ok) throw new Error(responseError(body, 'Failed to request review'));
    if (body.success === false) {
      throw new Error(REQUEST_REFUSAL_MESSAGES[body.reason] || 'Review request could not be sent.');
    }
    setEligibleOrders(previous => previous.map(item => (
      item.uuid === order.uuid
        ? {...item, reviewRequestedAt: body.reviewRequestedAt || new Date().toISOString()}
        : item
    )));
    setActionMessage('Review request sent.');
  };

  const total = reviewsMeta?.total ?? reviews.length;
  const recommendCount = reviews.filter(review => review.rating === 1).length;
  const recommendPct = reviews.length
    ? Math.round((recommendCount / reviews.length) * 100)
    : 0;
  const pendingReplies = reviewsMeta?.unrespondedCount
    ?? reviews.filter(review => !review.response).length;
  const publicReviewsUrl = safePublicReviewsUrl(reviewsMeta?.publicReviewsUrl);

  return (
    <div className="seller-reviews">
      <div className="seller-reviews__metrics">
        <div className="seller-reviews__metric">
          <div className="seller-reviews__metric-value">{total}</div>
          <div className="seller-reviews__metric-label">Total reviews</div>
        </div>
        <div className="seller-reviews__metric">
          <div className="seller-reviews__metric-value">{recommendPct}%</div>
          <div className="seller-reviews__metric-label">Would recommend</div>
        </div>
        <div className="seller-reviews__metric">
          <div className="seller-reviews__metric-value">{pendingReplies}</div>
          <div className="seller-reviews__metric-label">Pending replies</div>
        </div>
      </div>

      {publicReviewsUrl && (
        <p className="seller-reviews__public-link">
          <a href={publicReviewsUrl} target="_blank" rel="noopener noreferrer">
            View your public reviews page
          </a>
        </p>
      )}

      {actionMessage && <Alert type="success" role="status">{actionMessage}</Alert>}

      <div className="seller-reviews__tabs" role="tablist" aria-label="Reviews sections">
        {TABS.map((option, index) => (
          <button
            key={option.key}
            type="button"
            role="tab"
            id={`seller-reviews-tab-${option.key}`}
            ref={el => { tabRefs.current[index] = el; }}
            aria-selected={tab === option.key}
            aria-controls={`seller-reviews-panel-${option.key}`}
            tabIndex={tab === option.key ? 0 : -1}
            className={`seller-reviews__tab${tab === option.key ? ' is-active' : ''}`}
            onClick={() => setTab(option.key)}
            onKeyDown={event => handleTabKeyDown(event, index)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {tab === 'reviews' && (
        <div
          role="tabpanel"
          id="seller-reviews-panel-reviews"
          aria-labelledby="seller-reviews-tab-reviews"
          tabIndex={0}
        >
          {reviewsLoading && <LoadingSkeleton />}
          {!reviewsLoading && reviewsError && (
            <Alert type="error" role="alert">
              <p>{reviewsError}</p>
              <button type="button" onClick={loadReviews}>Try again</button>
            </Alert>
          )}
          {!reviewsLoading && !reviewsError && reviews.length === 0 && (
            <p className="seller-reviews__empty">
              You have not received any customer reviews yet.
            </p>
          )}
          {!reviewsLoading && !reviewsError && reviews.length > 0 && (
            <ReviewsTable reviews={reviews} onSelect={setSelectedReview} />
          )}
        </div>
      )}

      {tab === 'eligible' && (
        <div
          role="tabpanel"
          id="seller-reviews-panel-eligible"
          aria-labelledby="seller-reviews-tab-eligible"
          tabIndex={0}
        >
          {eligibleLoading && <LoadingSkeleton />}
          {!eligibleLoading && eligibleError && (
            <Alert type="error" role="alert">
              <p>{eligibleError}</p>
              <button type="button" onClick={loadEligibleOrders}>Try again</button>
            </Alert>
          )}
          {!eligibleLoading && !eligibleError && (
            <ReviewEligibleOrdersTable orders={eligibleOrders} onRequest={requestReview} />
          )}
        </div>
      )}

      {selectedReview && (
        <ReviewDetailModal
          review={selectedReview}
          onClose={() => setSelectedReview(null)}
          onSubmitResponse={submitResponse}
        />
      )}
    </div>
  );
}
